import type { AreaNavListGroupItem } from "@octopusdeploy/design-system-components";
import { Avatar } from "@octopusdeploy/design-system-octopus-components";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { UserFavouriteProjectSummary } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { useEffect } from "react";
import { isGitHubAppEnabled } from "~/areas/configuration/components/Git/getGitHubAppSettings";
import useIsMultiTenancyEnabledFeatureFlag from "~/areas/configuration/hooks/useIsMultiTenancyEnabledFeatureFlag";
import { useArgoIsNew } from "~/areas/infrastructure/components/ArgoCDInstances/useArgoIsNew";
import { client } from "~/clientInstance";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import type { SpaceContext } from "~/components/SpaceLoader/SpaceLoader";
import { isSpecificSpaceContext } from "~/components/SpaceLoader/SpaceLoader";
import { hasPermission, isAllowed } from "../PermissionCheck/PermissionCheck";
import type { AreaNavPanelConfig } from "./AreaNavPanelConfig";
import { hasPermissionsInAnyScope } from "./hasPermissionInAnyScope";
export function useDeployAreaNavPanel(spaceContext: SpaceContext): AreaNavPanelConfig | undefined {
    const isMultiTenancyEnabled = useIsMultiTenancyEnabledFeatureFlag();
    const argoIsNew = useArgoIsNew();
    const favouriteProjects = useFavouriteProjects();
    if (!isSpecificSpaceContext(spaceContext)) {
        return undefined;
    }
    const centralizedRetentionEnabled = isFeatureToggleEnabled("centralized-retention-lifecycle") || isFeatureToggleEnabled("centralized-retention-v2") || isFeatureToggleEnabled("centralized-retention-runbook");
    const octopusApprovalsEnabled = isFeatureToggleEnabled("octopus-approvals");
    return {
        items: [
            {
                label: "Projects",
                href: links.projectsPage.generateUrl({ spaceId: spaceContext.Id }),
            },
            "spacer",
            {
                heading: "My Favorites",
                items: getFavouriteProjectItems(spaceContext.Id, favouriteProjects),
                hasTrailingDivider: true,
            },
            {
                heading: undefined,
                items: [
                    {
                        label: "Tenants",
                        href: links.tenantsPage.generateUrl({ spaceId: spaceContext.Id }),
                        isVisible: hasPermissionsInAnyScope(spaceContext.Id, Permission.TenantView),
                    },
                    {
                        label: isFeatureToggleEnabled("extended-tag-sets") ? "Tag Sets" : "Tenant Tag Sets",
                        href: links.tagSetsPage.generateUrl({ spaceId: spaceContext.Id }),
                        isVisible: (isFeatureToggleEnabled("extended-tag-sets") || isMultiTenancyEnabled) && isAllowed({ permission: Permission.VariableView, wildcard: true }, { spaceId: spaceContext.Id }),
                    },
                    {
                        label: "Variable Sets",
                        href: links.variableSetsPage.generateUrl({ spaceId: spaceContext.Id }),
                        isVisible: isAllowed({ permission: [Permission.VariableView, Permission.LibraryVariableSetView], wildcard: true }, { spaceId: spaceContext.Id }),
                    },
                ],
                hasTrailingDivider: true,
            },
            {
                heading: "Infrastructure",
                items: [
                    {
                        label: "Overview",
                        href: links.infrastructureOverviewPage.generateUrl({ spaceId: spaceContext.Id }),
                        isVisible: isAllowed({ permission: [Permission.EnvironmentView, Permission.MachineView, Permission.WorkerView], wildcard: true }, { spaceId: spaceContext.Id }),
                    },
                    {
                        label: "Argo CD Instances",
                        href: links.argoCDInstancesPage.generateUrl({ spaceId: spaceContext.Id }),
                        showNewBadge: argoIsNew,
                        isVisible: isAllowed({ permission: [Permission.MachineView], wildcard: true }, { spaceId: spaceContext.Id }),
                    },
                    {
                        label: "Deployment Targets",
                        href: links.deploymentTargetsPage.generateUrl({ spaceId: spaceContext.Id }),
                        isVisible: isAllowed({ permission: [Permission.MachineView], wildcard: true }, { spaceId: spaceContext.Id }),
                    },
                    {
                        label: "Environments",
                        href: links.environmentsPage.generateUrl({ spaceId: spaceContext.Id }),
                        isVisible: isAllowed({ permission: [Permission.EnvironmentView, Permission.MachineView] }, { spaceId: spaceContext.Id }),
                    },
                    {
                        label: "Machine Policies",
                        href: links.machinePoliciesPage.generateUrl({ spaceId: spaceContext.Id }),
                        isVisible: isAllowed({ permission: Permission.MachinePolicyView, wildcard: true }, { spaceId: spaceContext.Id }),
                    },
                    {
                        label: "Machine Proxies",
                        href: links.machineProxiesPage.generateUrl({ spaceId: spaceContext.Id }),
                        isVisible: isAllowed({ permission: Permission.ProxyView, wildcard: true }, { spaceId: spaceContext.Id }),
                    },
                    {
                        label: "Workers",
                        href: links.workersPage.generateUrl({ spaceId: spaceContext.Id }),
                        isVisible: isAllowed({ permission: [Permission.WorkerView], wildcard: true }, { spaceId: spaceContext.Id }),
                    },
                    {
                        label: "Worker Pools",
                        href: links.workerPoolsPage.generateUrl({ spaceId: spaceContext.Id }),
                        isVisible: isAllowed({ permission: [Permission.EnvironmentView] }, { spaceId: spaceContext.Id }),
                    },
                ],
                hasTrailingDivider: true,
            },
            {
                heading: "Manage",
                items: [
                    {
                        label: "Accounts",
                        href: links.accountsPage.generateUrl({ spaceId: spaceContext.Id }),
                        isVisible: isAllowed({ permission: Permission.AccountView, wildcard: true }, { spaceId: spaceContext.Id }),
                    },
                    {
                        label: "Approval Policies",
                        href: links.approvalPoliciesPage.generateUrl({ spaceId: spaceContext.Id }),
                        isVisible: octopusApprovalsEnabled, // TODO: needs proper permission
                    },
                    {
                        label: "Build Information",
                        href: links.buildInformationRepositoryPage.generateUrl({ spaceId: spaceContext.Id }),
                        isVisible: isAllowed({ permission: Permission.FeedView }, { spaceId: spaceContext.Id }),
                    },
                    {
                        label: "Certificates",
                        href: links.certificatesPage.generateUrl({ spaceId: spaceContext.Id }),
                        isVisible: isAllowed({ permission: Permission.CertificateView, wildcard: true }, { spaceId: spaceContext.Id }),
                    },
                    {
                        label: "External Feeds",
                        href: links.externalFeedsPage.generateUrl({ spaceId: spaceContext.Id }),
                        isVisible: isAllowed({ permission: Permission.FeedView }, { spaceId: spaceContext.Id }),
                    },
                    {
                        label: "Git Credentials",
                        href: links.gitCredentialsPage.generateUrl({ spaceId: spaceContext.Id }),
                        isVisible: isAllowed({ permission: [Permission.GitCredentialView], wildcard: true }, { spaceId: spaceContext.Id }),
                    },
                    {
                        label: "GitHub Connections",
                        href: links.gitHubConnectionsPage.generateUrl({ spaceId: spaceContext.Id }),
                        isVisible: isGitHubAppEnabled() && isAllowed({ permission: [Permission.GitCredentialView], wildcard: true }, { spaceId: spaceContext.Id }),
                    },
                    {
                        label: "Lifecycles",
                        href: links.lifecyclesPage.generateUrl({ spaceId: spaceContext.Id }),
                        isVisible: isAllowed({ permission: Permission.LifecycleView, wildcard: true }, { spaceId: spaceContext.Id }),
                    },
                    {
                        label: "Packages",
                        href: links.builtInPackageRepositoryPage.generateUrl({ spaceId: spaceContext.Id }),
                        isVisible: isAllowed({ permission: Permission.FeedView }, { spaceId: spaceContext.Id }),
                    },
                    {
                        label: "Retention",
                        href: links.retentionPoliciesPage.generateUrl({ spaceId: spaceContext.Id }),
                        isVisible: centralizedRetentionEnabled,
                    },
                    {
                        label: "Script Modules",
                        href: links.scriptModulesPage.generateUrl({ spaceId: spaceContext.Id }),
                        isVisible: isAllowed({ permission: Permission.VariableView, wildcard: true }, { spaceId: spaceContext.Id }),
                    },
                    {
                        label: "Step Templates",
                        href: links.stepTemplatesPage.generateUrl({ spaceId: spaceContext.Id }),
                        isVisible: isAllowed({ permission: Permission.ActionTemplateView }, { spaceId: spaceContext.Id }),
                    },
                ],
            },
        ] as const,
    };
}
function useFavouriteProjects(): UserFavouriteProjectSummary[] | "not-loaded" {
    const { result, refetch } = useInlineStatusQuery(async (repository) => {
        if (hasPermission(Permission.ProjectView)) {
            const result = await repository.Users.getProjectFavourites();
            return result.Projects;
        }
        else {
            return [];
        }
    }, [], "Favourite projects");
    useEffect(() => {
        return client.subscribe((event) => {
            if (event.type === "UserFavouriteProjectCreated" || event.type === "UserFavouriteProjectDeleted") {
                refetch();
            }
        });
    }, [refetch]);
    return result ?? "not-loaded";
}
function getFavouriteProjectItems(spaceId: string, favouriteProjects: UserFavouriteProjectSummary[] | "not-loaded"): AreaNavListGroupItem[] {
    if (favouriteProjects === "not-loaded") {
        return [{ placeholderLabel: "..." }];
    }
    if (favouriteProjects.length === 0) {
        return [{ placeholderLabel: "No favorite projects" }];
    }
    return favouriteProjects.map((p) => ({
        label: p.Name,
        href: links.projectRootRedirect.generateUrl({ spaceId, projectSlug: p.Slug }),
        icon: <Avatar type="project" size="small" src={client.resolve(p.Logo)} alt={`${p.Name} Logo`}/>,
    }));
}
